import PERMISSIONS_DESCRIPTIONS from '~/server_shared/generated-types/PERMISSIONS_DESCRIPTIONS';

import { __DEFAULT_PROFILES__ } from './DefaultProfiles';
import { __PERMISSION_VERBS__ } from './PermissionVerbs';
import { ConfigEnumBase, InnerSectionItem, SectionHeader } from './types';

const ALL_VERBS = Object.values(__PERMISSION_VERBS__);
const READ_WRITE = [__PERMISSION_VERBS__.READ, __PERMISSION_VERBS__.WRITE];
const NONE_WRITE = [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.WRITE];
const NONE_READ_WRITE = [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.READ, __PERMISSION_VERBS__.WRITE];
const NONE_READ = [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.READ];

/**
 *
 * @type {InnerSectionsConfig}
 */
const DEFAULT_SECTIONS_DEFINITION = {
  DEFAULT: {
    id: 'DEFAULT',
    title: '',
  },
};

const DEFAULT_PROFILES = new Proxy(__DEFAULT_PROFILES__, {
  get(target, name) {
    return target[name]?.id;
  },
});

// Common default profiles for all Clive inner permissions (Chat, Claim Summary, etc.)
const CLIVE_INNER_PERMISSION_DEFAULT_PROFILES = {
  [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FULL,
  [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FULL,
  [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
  [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
  [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
};

// Shared by the SYSTEM_CONFIGURATION header and every granular scope under it. The scopes default
// to FORBIDDEN (their PERMISSIONS_DESCRIPTIONS default_verb), so without rows of their own a new
// organization's Supervisor and IT manager would hold the header and none of the areas it opens.
const SYSTEM_CONFIGURATION_DEFAULT_PROFILES = {
  [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
  [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FULL,
  [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
  [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FULL,
  [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
};

class PERMISSION_ACTIONS extends ConfigEnumBase {
  getSectionInnerItems(section) {
    return Object.values(this).filter((value) => value instanceof InnerSectionItem && value.section === section);
  }
  // Claim Section
  CLAIM = new SectionHeader({
    title: PERMISSIONS_DESCRIPTIONS.CLAIM.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CLAIM.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CLAIM.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.READ],
    sectionHeaderIndex: 0,
    innerSections: {
      GENERAL: {
        id: 'GENERAL',
        title: 'General',
      },
      PAYMENTS: {
        id: 'PAYMENTS',
        title: 'Payments',
      },
      DOCUMENTS_TAB: {
        id: 'DOCUMENTS_TAB',
        title: 'Documents Tab',
      },
      POLICY_TAB: {
        id: 'POLICY_TAB',
        title: 'Policy Tab',
      },
      LEGAL_ACTIONS: {
        id: 'LEGAL_ACTIONS',
        title: 'Legal Actions',
      },
    },
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  // Claim -> General
  DATE_OF_LOSS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.DATE_OF_LOSS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.DATE_OF_LOSS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.DATE_OF_LOSS.description,
    allowedVerbs: [__PERMISSION_VERBS__.READ, __PERMISSION_VERBS__.WRITE],
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  TIME_OF_LOSS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.TIME_OF_LOSS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.TIME_OF_LOSS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.TIME_OF_LOSS.description,
    allowedVerbs: [__PERMISSION_VERBS__.READ, __PERMISSION_VERBS__.WRITE],
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  DATE_OF_REPORT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.DATE_OF_REPORT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.DATE_OF_REPORT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.DATE_OF_REPORT.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  REPORTER = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.REPORTER.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.REPORTER.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.REPORTER.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  EXTERNAL_CLAIM_NUMBER = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.EXTERNAL_CLAIM_NUMBER.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.EXTERNAL_CLAIM_NUMBER.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.EXTERNAL_CLAIM_NUMBER.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  COVERAGE_DECISION = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.COVERAGE_DECISION.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.COVERAGE_DECISION.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.COVERAGE_DECISION.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 1,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  NOTE = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.NOTE.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.NOTE.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.NOTE.description,
    allowedVerbs: [__PERMISSION_VERBS__.READ, __PERMISSION_VERBS__.WRITE, __PERMISSION_VERBS__.FULL],
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 2,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  NOTIFICATION = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.NOTIFICATION.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.NOTIFICATION.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.NOTIFICATION.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 3,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CHANGE_CLAIM_OWNER = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CHANGE_CLAIM_OWNER.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CHANGE_CLAIM_OWNER.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CHANGE_CLAIM_OWNER.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 4,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CHANGE_EXPOSURE_OWNER = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CHANGE_EXPOSURE_OWNER.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CHANGE_EXPOSURE_OWNER.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CHANGE_EXPOSURE_OWNER.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 4,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  EXPORT_CLAIM = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.EXPORT_CLAIM.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.EXPORT_CLAIM.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.EXPORT_CLAIM.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 5,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  LEGAL_ACTIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.LEGAL_ACTIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.LEGAL_ACTIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.LEGAL_ACTIONS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.READ, __PERMISSION_VERBS__.WRITE],
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.LEGAL_ACTIONS,
    index: 6,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  // Claim -> Payments
  INDEMNITY_RESERVE = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.INDEMNITY_RESERVE.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.INDEMNITY_RESERVE.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.INDEMNITY_RESERVE.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  EXPENSES_RESERVE = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.EXPENSES_RESERVE.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.EXPENSES_RESERVE.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.EXPENSES_RESERVE.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 1,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  GENERAL_EXPENSES_RESERVE = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.GENERAL_EXPENSES_RESERVE.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.GENERAL_EXPENSES_RESERVE.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.GENERAL_EXPENSES_RESERVE.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 2,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  INDEMNITY_PAYMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.INDEMNITY_PAYMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.INDEMNITY_PAYMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.INDEMNITY_PAYMENT.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 3,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  EXPENSES_PAYMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.EXPENSES_PAYMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.EXPENSES_PAYMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.EXPENSES_PAYMENT.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 4,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  GENERAL_EXPENSES_PAYMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.GENERAL_EXPENSES_PAYMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.GENERAL_EXPENSES_PAYMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.GENERAL_EXPENSES_PAYMENT.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 5,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CANCEL_INDEMNITY_PAYMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CANCEL_INDEMNITY_PAYMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CANCEL_INDEMNITY_PAYMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CANCEL_INDEMNITY_PAYMENT.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 6,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CANCEL_EXPENSES_PAYMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CANCEL_EXPENSES_PAYMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CANCEL_EXPENSES_PAYMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CANCEL_EXPENSES_PAYMENT.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 7,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CANCEL_GENERAL_EXPENSES_PAYMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CANCEL_GENERAL_EXPENSES_PAYMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CANCEL_GENERAL_EXPENSES_PAYMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CANCEL_GENERAL_EXPENSES_PAYMENT.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 8,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  OFAC_OVERRIDE = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.OFAC_OVERRIDE.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.OFAC_OVERRIDE.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.OFAC_OVERRIDE.description,
    allowedVerbs: NONE_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 9,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  APPROVE_INDEMNITY_PAYMENT_OVER_LIMIT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.APPROVE_INDEMNITY_PAYMENT_OVER_LIMIT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.APPROVE_INDEMNITY_PAYMENT_OVER_LIMIT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.APPROVE_INDEMNITY_PAYMENT_OVER_LIMIT.description,
    allowedVerbs: NONE_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 9,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  RECOVERY_REQUEST = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.RECOVERY_REQUEST.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.RECOVERY_REQUEST.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.RECOVERY_REQUEST.description,
    allowedVerbs: NONE_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 9,
    requiredFeatureFlag: 'recovery_requests',
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  APPROVE_CHANGE_INDEMNITY_RESERVE_OVER_LIMIT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.APPROVE_CHANGE_INDEMNITY_RESERVE_OVER_LIMIT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.APPROVE_CHANGE_INDEMNITY_RESERVE_OVER_LIMIT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.APPROVE_CHANGE_INDEMNITY_RESERVE_OVER_LIMIT.description,
    allowedVerbs: NONE_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.PAYMENTS,
    index: 9,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  // Claim -> Documents Tab
  CLAIM_DOCUMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CLAIM_DOCUMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CLAIM_DOCUMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CLAIM_DOCUMENT.description,
    allowedVerbs: [__PERMISSION_VERBS__.READ, __PERMISSION_VERBS__.WRITE, __PERMISSION_VERBS__.FULL],
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.DOCUMENTS_TAB,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  // Claim -> Policy Tab
  MANUAL_POLICY = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.MANUAL_POLICY.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.MANUAL_POLICY.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.MANUAL_POLICY.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.POLICY_TAB,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  API_POLICY = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.API_POLICY.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.API_POLICY.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.API_POLICY.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.POLICY_TAB,
    index: 1,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  MOI = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.MOI.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.MOI.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.MOI.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 0,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  OVERRIDE_DEPRECIATION_CALCULATIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.OVERRIDE_DEPRECIATION_CALCULATIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.OVERRIDE_DEPRECIATION_CALCULATIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.OVERRIDE_DEPRECIATION_CALCULATIONS.description,
    allowedVerbs: READ_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 7,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.READ,
    },
  });
  CREATE_LIKE_CLAIM = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CREATE_LIKE_CLAIM.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CREATE_LIKE_CLAIM.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CREATE_LIKE_CLAIM.description,
    allowedVerbs: NONE_WRITE,
    section: this.CLAIM,
    innerSection: this.CLAIM.innerSections.GENERAL,
    index: 8,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  // Contact
  CONTACT = new SectionHeader({
    title: PERMISSIONS_DESCRIPTIONS.CONTACT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CONTACT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CONTACT.description,
    allowedVerbs: ALL_VERBS,
    sectionHeaderIndex: 1,
    innerSections: DEFAULT_SECTIONS_DEFINITION,
    preconditions: [],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CONTACT_PII = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CONTACT_PII.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CONTACT_PII.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CONTACT_PII.description,
    allowedVerbs: READ_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 0,
    preconditions: [
      {
        field: this.CONTACT,
        needsToBeAtLeast: __PERMISSION_VERBS__.WRITE,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.WRITE,
        errorMessage:
          'If the Contact PII permission is set to Write, the Contacts and Parties permission must also be Write or Full',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.READ,
    },
  });
  CONTACT_FINANCE_PII = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CONTACT_FINANCE_PII.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CONTACT_FINANCE_PII.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CONTACT_FINANCE_PII.description,
    allowedVerbs: READ_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 1,
    preconditions: [
      {
        field: this.CONTACT,
        needsToBeAtLeast: __PERMISSION_VERBS__.WRITE,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.WRITE,
        errorMessage:
          'If the Contact Finance PII permission is set to Write, the Contacts and Parties permission must also be Write or Full',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.READ,
    },
  });
  W9_TIN = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.W9_TIN.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.W9_TIN.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.W9_TIN.description,
    allowedVerbs: READ_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 2,
  });
  CONTACT_FINANCE_APPROVER = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CONTACT_FINANCE_APPROVER.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CONTACT_FINANCE_APPROVER.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CONTACT_FINANCE_APPROVER.description,
    allowedVerbs: READ_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 3,
    requiredPaymentsConfig: 'require_verification_for_contact_finance_edit',
    preconditions: [
      {
        field: this.CONTACT,
        needsToBeAtLeast: __PERMISSION_VERBS__.WRITE,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.WRITE,
        errorMessage:
          'If the Contact Finance Approver permission is set to Write, the Contacts and Parties permission must also be Write or Full',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CONTACT_TFN_APPROVER = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CONTACT_TFN_APPROVER.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CONTACT_TFN_APPROVER.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CONTACT_TFN_APPROVER.description,
    allowedVerbs: READ_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 4,
    requiredPaymentsConfig: 'require_verification_for_contact_au_tfn_edit',
    preconditions: [
      {
        field: this.CONTACT,
        needsToBeAtLeast: __PERMISSION_VERBS__.WRITE,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.WRITE,
        errorMessage:
          'If the Contact TFN Approver permission is set to Write, the Contacts and Parties permission must also be Write or Full',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  UNLINK_CLAIM_CONTACTS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.UNLINK_CLAIM_CONTACTS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.UNLINK_CLAIM_CONTACTS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.UNLINK_CLAIM_CONTACTS.description,
    allowedVerbs: NONE_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 5,
    preconditions: [
      {
        field: this.CONTACT,
        needsToBeAtLeast: __PERMISSION_VERBS__.WRITE,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.WRITE,
        errorMessage:
          'If the Unlink Claim Contacts permission is set to Write, the Contacts and Parties permission must also be Write or Full',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  DEACTIVATE_CONTACT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.DEACTIVATE_CONTACT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.DEACTIVATE_CONTACT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.DEACTIVATE_CONTACT.description,
    allowedVerbs: NONE_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 6,
    preconditions: [
      {
        field: this.CONTACT,
        needsToBeAtLeast: __PERMISSION_VERBS__.WRITE,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.WRITE,
        errorMessage:
          'If the Deactivate Contact permission is set to Write, the Contacts and Parties permission must also be Write or Full',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  DELETE_INVOLVED_PARTY = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.DELETE_INVOLVED_PARTY.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.DELETE_INVOLVED_PARTY.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.DELETE_INVOLVED_PARTY.description,
    allowedVerbs: NONE_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 7,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  TRUSTED_PAYEE_ARRANGEMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.TRUSTED_PAYEE_ARRANGEMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.TRUSTED_PAYEE_ARRANGEMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.TRUSTED_PAYEE_ARRANGEMENT.description,
    allowedVerbs: READ_WRITE,
    section: this.CONTACT,
    innerSection: this.CONTACT.innerSections.DEFAULT,
    index: 8,
    requiredPaymentsConfig: 'enable_trusted_payee_payments',
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CREATE_FNOL = new SectionHeader({
    title: PERMISSIONS_DESCRIPTIONS.CREATE_FNOL.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CREATE_FNOL.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CREATE_FNOL.description,
    allowedVerbs: NONE_WRITE,
    sectionHeaderIndex: 2,
    innerSections: DEFAULT_SECTIONS_DEFINITION,
    preconditions: [
      {
        field: this.CONTACT,
        needsToBeAtLeast: __PERMISSION_VERBS__.WRITE,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.WRITE,
        errorMessage: 'If the FNOL permission is set to Write, the Contacts and Parties permission must also be Write',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  QA = new SectionHeader({
    title: PERMISSIONS_DESCRIPTIONS.QA.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.QA.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.QA.description,
    allowedVerbs: NONE_WRITE,
    sectionHeaderIndex: 3,
    innerSections: DEFAULT_SECTIONS_DEFINITION,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  GENERAL_EMAIL_CLASSIFICATION = new SectionHeader({
    title: PERMISSIONS_DESCRIPTIONS.GENERAL_EMAIL_CLASSIFICATION.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.GENERAL_EMAIL_CLASSIFICATION.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.GENERAL_EMAIL_CLASSIFICATION.description,
    allowedVerbs: NONE_READ_WRITE,
    sectionHeaderIndex: 4,
    innerSections: DEFAULT_SECTIONS_DEFINITION,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.WRITE,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  REPORT = new SectionHeader({
    title: PERMISSIONS_DESCRIPTIONS.REPORT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.REPORT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.REPORT.description,
    allowedVerbs: NONE_READ_WRITE,
    sectionHeaderIndex: 5,
    innerSections: DEFAULT_SECTIONS_DEFINITION,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  ADJUSTER_DASHBOARD = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.ADJUSTER_DASHBOARD.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.ADJUSTER_DASHBOARD.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.ADJUSTER_DASHBOARD.description,
    allowedVerbs: NONE_READ,
    section: this.REPORT,
    innerSection: this.REPORT.innerSections.DEFAULT,
    index: 0,
    preconditions: [
      {
        field: this.REPORT,
        needsToBeAtLeast: __PERMISSION_VERBS__.READ,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.READ,
        errorMessage:
          'If the Adjuster Dashboard permission is set to Read, the Report and Dashboards permission must also be Read',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  MANAGEMENT_REPORTS_AND_DASHBOARDS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.MANAGEMENT_REPORTS_AND_DASHBOARDS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.MANAGEMENT_REPORTS_AND_DASHBOARDS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.MANAGEMENT_REPORTS_AND_DASHBOARDS.description,
    allowedVerbs: NONE_READ,
    section: this.REPORT,
    innerSection: this.REPORT.innerSections.DEFAULT,
    index: 1,
    preconditions: [
      {
        field: this.REPORT,
        needsToBeAtLeast: __PERMISSION_VERBS__.READ,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.READ,
        errorMessage:
          'If the Management Reports and Dashboards permission is set to Read, the Report and Dashboards permission must also be Read',
      },
    ],
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.READ,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  VIEW_GENERAL_REPORTS_SUITE = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.VIEW_GENERAL_REPORTS_SUITE.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.VIEW_GENERAL_REPORTS_SUITE.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.VIEW_GENERAL_REPORTS_SUITE.description,
    allowedVerbs: NONE_READ,
    section: this.REPORT,
    innerSection: this.REPORT.innerSections.DEFAULT,
    index: 1,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  CREATE_REPORTS_AND_DASHBOARDS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CREATE_REPORTS_AND_DASHBOARDS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CREATE_REPORTS_AND_DASHBOARDS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CREATE_REPORTS_AND_DASHBOARDS.description,
    allowedVerbs: NONE_WRITE,
    section: this.REPORT,
    innerSection: this.REPORT.innerSections.DEFAULT,
    index: 1,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  SYSTEM_CONFIGURATION = new SectionHeader({
    title: PERMISSIONS_DESCRIPTIONS.SYSTEM_CONFIGURATION.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSTEM_CONFIGURATION.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSTEM_CONFIGURATION.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    sectionHeaderIndex: 6,
    innerSections: DEFAULT_SECTIONS_DEFINITION,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  // Granular System Configuration permission scopes. Each gates one area of the
  // sysconfig screen (full-or-none); the SYSTEM_CONFIGURATION header itself
  // gates entry to the sysconfig surface as a whole.
  SYSCONFIG_USER_MANAGEMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_USER_MANAGEMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_USER_MANAGEMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_USER_MANAGEMENT.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 0,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_ADJUSTER_MANAGEMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_ADJUSTER_MANAGEMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_ADJUSTER_MANAGEMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_ADJUSTER_MANAGEMENT.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 1,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_AUTOMATION = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_AUTOMATION.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_AUTOMATION.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_AUTOMATION.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 2,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_COMMUNICATIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_COMMUNICATIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_COMMUNICATIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_COMMUNICATIONS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 3,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_NOTIFICATIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_NOTIFICATIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_NOTIFICATIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_NOTIFICATIONS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 4,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_CLAIM_CONFIG = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CLAIM_CONFIG.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CLAIM_CONFIG.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CLAIM_CONFIG.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 5,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  // One scope for both tiles: the Policy and FNOL screens edit the same incident-configuration
  // records through the same routes, so splitting them would only manufacture a dependency.
  SYSCONFIG_POLICY_FNOL = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_POLICY_FNOL.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_POLICY_FNOL.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_POLICY_FNOL.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 6,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_TEMPLATES = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_TEMPLATES.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_TEMPLATES.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_TEMPLATES.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 7,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_RESERVES = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_RESERVES.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_RESERVES.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_RESERVES.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 8,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_DATA_REPORTING = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_DATA_REPORTING.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_DATA_REPORTING.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_DATA_REPORTING.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 9,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_LOGIN = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_LOGIN.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_LOGIN.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_LOGIN.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 10,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_SUB_ORGANIZATIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_SUB_ORGANIZATIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_SUB_ORGANIZATIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_SUB_ORGANIZATIONS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 11,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_LOCATIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_LOCATIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_LOCATIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_LOCATIONS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 12,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_CAT_EVENTS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CAT_EVENTS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CAT_EVENTS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CAT_EVENTS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 13,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_PERMISSIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_PERMISSIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_PERMISSIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_PERMISSIONS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 14,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_INTEGRATIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_INTEGRATIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_INTEGRATIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_INTEGRATIONS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 15,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_DELEGATED_AUTHORITY = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_DELEGATED_AUTHORITY.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_DELEGATED_AUTHORITY.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_DELEGATED_AUTHORITY.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 16,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_PERFORMANCE_QA = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_PERFORMANCE_QA.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_PERFORMANCE_QA.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_PERFORMANCE_QA.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 17,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_CONFIG_MANAGEMENT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CONFIG_MANAGEMENT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CONFIG_MANAGEMENT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CONFIG_MANAGEMENT.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 18,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_CONTACT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CONTACT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CONTACT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CONTACT.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 19,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_CHAT_SUGGESTIONS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CHAT_SUGGESTIONS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CHAT_SUGGESTIONS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_CHAT_SUGGESTIONS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 20,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_SYSTEM_OF_RECORD = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_SYSTEM_OF_RECORD.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_SYSTEM_OF_RECORD.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_SYSTEM_OF_RECORD.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 21,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  SYSCONFIG_GLOBAL_FIELDS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_GLOBAL_FIELDS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_GLOBAL_FIELDS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SYSCONFIG_GLOBAL_FIELDS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 22,
    defaultProfiles: SYSTEM_CONFIGURATION_DEFAULT_PROFILES,
  });
  // Not a SYSCONFIG_ scope: the System of Record Issues tab is granted apart from configuration rights.
  SOR_ISSUES_MANAGE = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.SOR_ISSUES_MANAGE.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.SOR_ISSUES_MANAGE.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.SOR_ISSUES_MANAGE.description,
    allowedVerbs: NONE_READ_WRITE,
    section: this.SYSTEM_CONFIGURATION,
    innerSection: this.SYSTEM_CONFIGURATION.innerSections.DEFAULT,
    index: 23,
    requiredFeatureFlag: 'system_of_record',
  });
  // Clive
  CLIVE = new SectionHeader({
    title: PERMISSIONS_DESCRIPTIONS.CLIVE.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CLIVE.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CLIVE.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    sectionHeaderIndex: 7,
    innerSections: DEFAULT_SECTIONS_DEFINITION,
    defaultProfiles: {
      [DEFAULT_PROFILES.ADJUSTER]: __PERMISSION_VERBS__.FULL,
      [DEFAULT_PROFILES.SUPERVISOR]: __PERMISSION_VERBS__.FULL,
      [DEFAULT_PROFILES.PHONE_REP]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.IT_MANAGER]: __PERMISSION_VERBS__.FORBIDDEN,
      [DEFAULT_PROFILES.EXTERNAL_USER]: __PERMISSION_VERBS__.FORBIDDEN,
    },
  });
  // Helper to generate preconditions for Clive inner permissions (Chat, Claim Summary, etc.)
  _getCliveInnerPermissionPreconditions(permissionDisplayName) {
    // Skip permissions that don't allow Read (e.g. NONE_WRITE approval toggles) —
    // a "must be at least Read" rule would be impossible for the user to satisfy.
    // allowedVerbs holds verb ids (strings) after the base-class constructor mapping
    // in types.js, so compare against READ.id, not the verb object itself.
    const canBeReadable = (item) => item.allowedVerbs.includes(__PERMISSION_VERBS__.READ.id);
    return [
      {
        field: this.CLIVE,
        needsToBeAtLeast: __PERMISSION_VERBS__.FULL,
        whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.FULL,
        errorMessage: `If the ${permissionDisplayName} permission is set to Full, the Clive permission must also be Full`,
      },
      ...this.getSectionInnerItems(this.CLAIM)
        .filter(canBeReadable)
        .map((item) => ({
          field: item,
          needsToBeAtLeast: __PERMISSION_VERBS__.READ,
          whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.READ,
          errorMessage: `To access ${permissionDisplayName}, the ${item.title} permission, in the ${this.CLAIM.title} section, must be at least Read`,
        })),
      ...this.getSectionInnerItems(this.CONTACT)
        .filter(canBeReadable)
        .map((item) => ({
          field: item,
          needsToBeAtLeast: __PERMISSION_VERBS__.READ,
          whenVerbIsEqualOrGreaterThan: __PERMISSION_VERBS__.READ,
          errorMessage: `To access ${permissionDisplayName}, the ${item.title} permission, in the ${this.CONTACT.title} section, must be at least Read`,
        })),
    ];
  }

  CLIVE_CHAT = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CLIVE_CHAT.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CLIVE_CHAT.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CLIVE_CHAT.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.CLIVE,
    innerSection: this.CLIVE.innerSections.DEFAULT,
    index: 0,
    preconditions: this._getCliveInnerPermissionPreconditions('Clive Chat'),
    defaultProfiles: CLIVE_INNER_PERMISSION_DEFAULT_PROFILES,
  });
  CLIVE_CLAIM_SUMMARY = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CLIVE_CLAIM_SUMMARY.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CLIVE_CLAIM_SUMMARY.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CLIVE_CLAIM_SUMMARY.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.CLIVE,
    innerSection: this.CLIVE.innerSections.DEFAULT,
    index: 1,
    preconditions: this._getCliveInnerPermissionPreconditions('Clive Claim Summary'),
    defaultProfiles: CLIVE_INNER_PERMISSION_DEFAULT_PROFILES,
  });
  CLIVE_CLAIM_INSIGHTS = new InnerSectionItem({
    title: PERMISSIONS_DESCRIPTIONS.CLIVE_CLAIM_INSIGHTS.title,
    subtitle: PERMISSIONS_DESCRIPTIONS.CLIVE_CLAIM_INSIGHTS.subtitle,
    description: PERMISSIONS_DESCRIPTIONS.CLIVE_CLAIM_INSIGHTS.description,
    allowedVerbs: [__PERMISSION_VERBS__.FORBIDDEN, __PERMISSION_VERBS__.FULL],
    section: this.CLIVE,
    innerSection: this.CLIVE.innerSections.DEFAULT,
    index: 2,
    preconditions: this._getCliveInnerPermissionPreconditions('Clive Claim Insights'),
    defaultProfiles: CLIVE_INNER_PERMISSION_DEFAULT_PROFILES,
  });

  constructor() {
    super();
    this._setKeysOnChildren();

    Object.values(this).forEach((value) => {
      if (value instanceof SectionHeader && value.preconditions) {
        value.preconditions.forEach((precondition) => {
          if (typeof precondition.field === 'string') {
            precondition.field = this[precondition.field];
          }
        });
      }
    });
  }
}

const __PERMISSION_ACTIONS__ = new PERMISSION_ACTIONS();

export { __PERMISSION_ACTIONS__ };
